/** Ladenplan bearbeiten (Regale › Ladenplan): Raster, Grundriss-Bild, Wände/Eingang/Kasse/Beschriftung, Regale per Drag & Drop. Speichert automatisch. */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select, Split } from "../ui/Layout";
import { Ladenplan, PlanLegende } from "../ui/Ladenplan";
import { ApiFehler } from "../api/client";
import { ELEMENT_TEXT, freierPlatz, PLAN_MAX, PLAN_MIN, planPruefen, type PlanElement, type PlanElementArt, type PlanPlatz } from "@shared/plan";
import { usePlan, usePlanHintergrund, usePlanSpeichern, type PlanDaten } from "./regale-api";

type Ziel = { art: "regal" | "element"; id: string };

export function LadenplanSeite() {
  const q = usePlan();
  return (
    <>
      <PageHeader kicker={<Link to="/laden/regale">Regale</Link>} title="Ladenplan" sub="Regale aufs Raster ziehen, Größe an der Ecke ändern; Wände, Eingang und Kasse als Orientierung. Änderungen werden automatisch gespeichert." />
      <Seitenzustand query={q} lines={8}>{(d) => <Editor daten={d} />}</Seitenzustand>
    </>
  );
}

function Editor({ daten }: { daten: PlanDaten }) {
  const speichern = usePlanSpeichern();
  const hintergrund = usePlanHintergrund();
  const [spalten, setSpalten] = useState(daten.plan.spalten);
  const [zeilen, setZeilen] = useState(daten.plan.zeilen);
  const [elemente, setElemente] = useState<PlanElement[]>(daten.plan.elemente);
  const [plaetze, setPlaetze] = useState<Record<string, PlanPlatz | null>>(Object.fromEntries(daten.regale.map((r) => [r.id, r.platz])));
  const [auswahl, setAuswahl] = useState<Ziel | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [stand, setStand] = useState<"gespeichert" | "offen" | "speichert">("gespeichert");
  const ersterLauf = useRef(true);
  const [bildStand, setBildStand] = useState(0);

  const regale = useMemo(() => daten.regale.map((r) => ({ ...r, platz: plaetze[r.id] ?? null })), [daten.regale, plaetze]);
  const nichtPlatziert = regale.filter((r) => !r.platz);

  // Automatisch speichern (800 ms nach der letzten Änderung), vorher lokal prüfen
  useEffect(() => {
    if (ersterLauf.current) {
      ersterLauf.current = false;
      return;
    }
    const eingabe = { spalten, zeilen, elemente, regale: regale.map((r) => ({ id: r.id, nummer: r.nummer, platz: r.platz })) };
    const lokal = planPruefen(eingabe);
    if (lokal) {
      setFehler(lokal);
      setStand("offen");
      return;
    }
    setFehler(null);
    setStand("offen");
    const t = setTimeout(() => {
      setStand("speichert");
      speichern.mutate({ spalten, zeilen, elemente, regale: eingabe.regale.map((r) => ({ id: r.id, platz: r.platz })) }, { onSuccess: () => setStand("gespeichert"), onError: (e) => { setStand("offen"); setFehler(e instanceof ApiFehler ? e.message : "Speichern hat nicht geklappt."); } });
    }, 800);
    return () => clearTimeout(t);
  }, [spalten, zeilen, elemente, plaetze]);

  function setzePlatz(ziel: Ziel, platz: PlanPlatz) {
    if (ziel.art === "regal") setPlaetze((p) => ({ ...p, [ziel.id]: platz }));
    else setElemente((els) => els.map((e) => (e.id === ziel.id ? { ...e, ...platz } : e)));
  }
  function platzieren(regalId: string) {
    const p = freierPlatz({ spalten, zeilen, elemente, plaetze: regale.map((r) => r.platz).filter((x): x is PlanPlatz => !!x) }, 2, 1);
    if (!p) {
      setFehler("Kein freies Feld mehr – Raster vergrößern oder Elemente verschieben.");
      return;
    }
    setPlaetze((pl) => ({ ...pl, [regalId]: p }));
    setAuswahl({ art: "regal", id: regalId });
  }
  function elementHinzufuegen(art: PlanElementArt) {
    const groesse = art === "wand" ? { b: 6, h: 1 } : art === "text" ? { b: 4, h: 1 } : { b: 2, h: 1 };
    const p = freierPlatz({ spalten, zeilen, elemente, plaetze: regale.map((r) => r.platz).filter((x): x is PlanPlatz => !!x) }, groesse.b, groesse.h) ?? { x: 0, y: 0, ...groesse };
    const el: PlanElement = { id: `el-${Date.now().toString(36)}`, art, text: "", ...p };
    setElemente((els) => [...els, el]);
    setAuswahl({ art: "element", id: el.id });
  }
  const gewaehltesRegal = auswahl?.art === "regal" ? regale.find((r) => r.id === auswahl.id) : undefined;
  const gewaehltesElement = auswahl?.art === "element" ? elemente.find((e) => e.id === auswahl.id) : undefined;
  const drehen = () => {
    if (gewaehltesRegal?.platz) {
      const p = gewaehltesRegal.platz;
      setzePlatz(auswahl!, { ...p, b: p.h, h: p.b, x: Math.min(p.x, spalten - p.h), y: Math.min(p.y, zeilen - p.b) });
    } else if (gewaehltesElement) {
      const p = gewaehltesElement;
      setzePlatz(auswahl!, { x: Math.min(p.x, spalten - p.h), y: Math.min(p.y, zeilen - p.b), b: p.h, h: p.b });
    }
  };

  return (
    <Split
      main={
        <>
          {fehler ? <Notice tone="attention">{fehler}</Notice> : null}
          <Card
            title="Plan"
            actions={stand === "gespeichert" ? <StatusBadge status="paid">Gespeichert</StatusBadge> : stand === "speichert" ? <StatusBadge status="wait">Speichert …</StatusBadge> : <StatusBadge status="attention">Nicht gespeichert</StatusBadge>}
          >
            <Ladenplan spalten={spalten} zeilen={zeilen} elemente={elemente} regale={regale} hintergrundUrl={daten.plan.hintergrundKey ? `/api/laden/dateien/${daten.plan.hintergrundKey}?v=${bildStand}` : null} bearbeitbar ausgewaehlt={auswahl} onAuswahl={setAuswahl} onPlatz={setzePlatz} />
            <div style={{ marginTop: "var(--space-3)" }}><PlanLegende /></div>
          </Card>
        </>
      }
      aside={
        <>
          {gewaehltesRegal ? (
            <Card title={`Regal ${gewaehltesRegal.nummer}`}>
              <p className="sc-muted">{gewaehltesRegal.lage || "–"}{gewaehltesRegal.mieterName ? ` · ${gewaehltesRegal.mieterName}` : ""}</p>
              {gewaehltesRegal.platz ? (
                <FormGrid>
                  <TextField label="Breite (Felder)" type="number" min={1} max={spalten} value={gewaehltesRegal.platz.b} onChange={(e) => setzePlatz(auswahl!, { ...gewaehltesRegal.platz!, b: Math.max(1, Math.min(spalten - gewaehltesRegal.platz!.x, Number(e.target.value) || 1)) })} />
                  <TextField label="Tiefe (Felder)" type="number" min={1} max={zeilen} value={gewaehltesRegal.platz.h} onChange={(e) => setzePlatz(auswahl!, { ...gewaehltesRegal.platz!, h: Math.max(1, Math.min(zeilen - gewaehltesRegal.platz!.y, Number(e.target.value) || 1)) })} />
                </FormGrid>
              ) : null}
              <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }}>
                <Button variant="secondary" icon="undo-2" onClick={drehen}>Drehen</Button>
                <Button variant="quiet" icon="x" onClick={() => { setPlaetze((p) => ({ ...p, [gewaehltesRegal.id]: null })); setAuswahl(null); }}>Vom Plan nehmen</Button>
                <Link to={`/laden/regale/${gewaehltesRegal.id}`} className="rg-btn rg-btn--quiet">Regal öffnen</Link>
              </div>
            </Card>
          ) : gewaehltesElement ? (
            <Card title={ELEMENT_TEXT[gewaehltesElement.art]}>
              {gewaehltesElement.art !== "wand" ? <TextField label="Beschriftung" value={gewaehltesElement.text} maxLength={30} onChange={(e) => setElemente((els) => els.map((x) => (x.id === gewaehltesElement.id ? { ...x, text: e.target.value } : x)))} /> : null}
              <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }}>
                <Button variant="secondary" icon="undo-2" onClick={drehen}>Drehen</Button>
                <Button variant="quiet" icon="x" onClick={() => { setElemente((els) => els.filter((x) => x.id !== gewaehltesElement.id)); setAuswahl(null); }}>Entfernen</Button>
              </div>
            </Card>
          ) : (
            <Card title="Bearbeiten">
              <p className="sc-muted">Ein Regal oder Element im Plan antippen, dann verschieben; an der Ecke die Größe ziehen.</p>
            </Card>
          )}
          <Card title={`Noch nicht im Plan · ${nichtPlatziert.length}`}>
            {nichtPlatziert.length === 0 ? <p className="sc-muted">Alle Regale sind platziert.</p> : null}
            <div className="sc-toolbar">
              {nichtPlatziert.map((r) => (
                <button key={r.id} type="button" className="sc-pill" onClick={() => platzieren(r.id)} title={r.lage}>+ {r.nummer}</button>
              ))}
            </div>
          </Card>
          <Card title="Elemente">
            <div className="sc-toolbar">
              {(["wand", "tuer", "kasse", "text"] as PlanElementArt[]).map((art) => (
                <Button key={art} variant="secondary" icon="plus" onClick={() => elementHinzufuegen(art)}>{ELEMENT_TEXT[art]}</Button>
              ))}
            </div>
          </Card>
          <Card title="Raster und Grundriss">
            <FormGrid>
              <Select label="Breite (Felder)" value={spalten} onChange={(e) => setSpalten(Number(e.target.value))}>
                {Array.from({ length: (PLAN_MAX.spalten - PLAN_MIN.spalten) / 4 + 1 }, (_, i) => PLAN_MIN.spalten + i * 4).map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
              <Select label="Tiefe (Felder)" value={zeilen} onChange={(e) => setZeilen(Number(e.target.value))}>
                {Array.from({ length: (PLAN_MAX.zeilen - PLAN_MIN.zeilen) / 2 + 1 }, (_, i) => PLAN_MIN.zeilen + i * 2).map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
            </FormGrid>
            <p className="sc-muted sc-small">Ein Feld entspricht z. B. 50 cm. Regale außerhalb des Rasters werden beim Verkleinern nicht verschoben – vorher umstellen.</p>
            <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }}>
              <label className="rg-btn rg-btn--secondary">
                {hintergrund.isPending ? "Lädt …" : daten.plan.hintergrundKey ? "Grundriss ersetzen" : "Grundriss-Bild hochladen"}
                <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(ev) => { const f = ev.target.files?.[0]; if (f) hintergrund.mutate(f, { onSuccess: () => setBildStand(Date.now()), onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Upload fehlgeschlagen.") }); ev.target.value = ""; }} />
              </label>
              {daten.plan.hintergrundKey ? <Button variant="quiet" icon="x" onClick={() => hintergrund.mutate(null)}>Grundriss entfernen</Button> : null}
            </div>
            <p className="app-help">PNG/JPEG/WebP bis 3 MB; wird halbtransparent unter das Raster gelegt.</p>
          </Card>
        </>
      }
    />
  );
}
